import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PaymentVouchers from "./expense/PaymentVouchers";
import LocalPurchaseOrders from "./expense/LocalPurchaseOrders";
import LocalServiceOrders from "./expense/LocalServiceOrders";
import { Receipt, ShoppingCart, Wrench, TrendingUp } from "lucide-react";

const ExpenseManagement = () => {
  const [activeTab, setActiveTab] = useState("overview");

  // Summary statistics
  const summaryStats = [
    {
      title: "Total Payment Vouchers",
      value: "124",
      trend: "+8 this month",
      icon: Receipt,
      color: "text-blue-600"
    },
    {
      title: "Active LPOs",
      value: "18",
      trend: "12 delivered",
      icon: ShoppingCart,
      color: "text-green-600"
    },
    {
      title: "Active LSOs",
      value: "7",
      trend: "3 in progress",
      icon: Wrench,
      color: "text-orange-600"
    },
    {
      title: "Total Expenditure",
      value: "Ksh 2.4M",
      trend: "+12% vs last month",
      icon: TrendingUp,
      color: "text-purple-600"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold">Track Expenses</h2>
        <p className="text-muted-foreground mt-1">
          Manage Payment Vouchers, Local Purchase Orders, and Service Orders
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryStats.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold mt-2">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.trend}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="payment-vouchers">
            <Receipt className="h-4 w-4 mr-2" />
            Payment Vouchers
          </TabsTrigger>
          <TabsTrigger value="lpo">
            <ShoppingCart className="h-4 w-4 mr-2" />
            LPOs
          </TabsTrigger>
          <TabsTrigger value="lso">
            <Wrench className="h-4 w-4 mr-2" />
            LSOs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Expense Activity</CardTitle>
                <CardDescription>Latest transactions across all expense types</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 pb-3 border-b">
                    <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                      <Receipt className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">PV-2024-045 Approved</p>
                      <p className="text-xs text-muted-foreground">Office supplies - Ksh 15,000</p>
                      <p className="text-xs text-muted-foreground mt-1">2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 pb-3 border-b">
                    <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                      <ShoppingCart className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">LPO-2024-023 Delivered</p>
                      <p className="text-xs text-muted-foreground">Computer equipment - Ksh 450,000</p>
                      <p className="text-xs text-muted-foreground mt-1">5 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
                      <Wrench className="h-4 w-4 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">LSO-2024-012 In Progress</p>
                      <p className="text-xs text-muted-foreground">Plumbing repairs - Ksh 45,000</p>
                      <p className="text-xs text-muted-foreground mt-1">1 day ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Approvals */}
            <Card>
              <CardHeader>
                <CardTitle>Pending Approvals</CardTitle>
                <CardDescription>Items requiring your attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer">
                    <div>
                      <p className="text-sm font-medium">PV-2024-046</p>
                      <p className="text-xs text-muted-foreground">Utilities payment</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">Ksh 35,000</p>
                      <p className="text-xs text-yellow-600">Pending</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer">
                    <div>
                      <p className="text-sm font-medium">LPO-2024-024</p>
                      <p className="text-xs text-muted-foreground">Library books</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">Ksh 125,000</p>
                      <p className="text-xs text-yellow-600">Pending</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer">
                    <div>
                      <p className="text-sm font-medium">LSO-2024-013</p>
                      <p className="text-xs text-muted-foreground">Cleaning services</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">Ksh 25,000</p>
                      <p className="text-xs text-yellow-600">Pending</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Expense Breakdown</CardTitle>
              <CardDescription>Expenses by category for current month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { category: "Salaries & Wages", amount: 850000, color: "bg-blue-500" },
                  { category: "Utilities", amount: 125000, color: "bg-green-500" },
                  { category: "Equipment & Supplies", amount: 450000, color: "bg-purple-500" },
                  { category: "Maintenance & Repairs", amount: 180000, color: "bg-orange-500" },
                  { category: "Services", amount: 95000, color: "bg-red-500" }
                ].map((item) => (
                  <div key={item.category}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">{item.category}</span>
                      <span className="text-sm font-bold">Ksh {item.amount.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${item.color}`}
                        style={{ width: `${(item.amount / 850000) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payment-vouchers" className="mt-6">
          <PaymentVouchers />
        </TabsContent>

        <TabsContent value="lpo" className="mt-6">
          <LocalPurchaseOrders />
        </TabsContent>

        <TabsContent value="lso" className="mt-6">
          <LocalServiceOrders />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExpenseManagement;